import { createFileRoute, Link } from "@tanstack/react-router";
import { sanityClient, urlFor } from "@/lib/sanity";
import {
  useLocale,
  pickLocalized,
  tr,
  t,
  formatDate,
  DEFAULT_LOCALE,
  type Locale,
} from "@/lib/i18n";
import { pageMeta, ogImageUrl, newsListingLd, breadcrumbLd, JsonLd, SITE_NAME } from "@/lib/seo";

const QUERY = `{
  "page": *[_type == "sitePage" && pageKey == "news"][0]{ seo },
  "posts": *[_type == "newsPost"] | order(publishedAt desc){
    _id, title, slug, excerpt, coverImage, publishedAt, kind
  }
}`;

export const Route = createFileRoute("/news")({
  component: NewsPage,
  loader: () => sanityClient.fetch<any>(QUERY),
  head: ({ loaderData }) =>
    pageMeta({
      title: pickLocalized(loaderData?.page?.seo?.metaTitle, DEFAULT_LOCALE) || "News — Matchamind",
      description:
        pickLocalized(loaderData?.page?.seo?.metaDescription, DEFAULT_LOCALE) || undefined,
      path: "/news",
    }),
});

function kindLabel(kind: string | undefined, locale: Locale): string | undefined {
  const entry = t.news.kinds[kind as keyof typeof t.news.kinds];
  return entry ? tr(entry, locale) : undefined;
}

function NewsPage() {
  const locale = useLocale();
  const search = locale === "en" ? {} : { lang: "fr" as const };
  const data = Route.useLoaderData();
  const posts = (data?.posts ?? []).filter((p: any) => p?.slug?.en?.current);

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 md:px-6 md:py-24">
      {posts.length > 0 && (
        <JsonLd
          data={newsListingLd({
            name:
              pickLocalized(data?.page?.seo?.metaTitle, DEFAULT_LOCALE)?.trim() ||
              `${SITE_NAME} News`,
            description:
              pickLocalized(data?.page?.seo?.metaDescription, DEFAULT_LOCALE) || undefined,
            posts: posts.map((p: any) => ({
              title: p.title,
              path: `/news/${p.slug.en.current}`,
              description: pickLocalized(p.excerpt, DEFAULT_LOCALE) || undefined,
              image: ogImageUrl(p.coverImage),
              datePublished: p.publishedAt,
            })),
          })}
        />
      )}
      <JsonLd
        data={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "News", path: "/news" },
        ])}
      />
      <h1 className="text-4xl font-semibold md:text-5xl">{tr(t.nav.news, locale)}</h1>
      <p className="mt-3 text-muted-foreground">
        {locale === "fr" ? "Annonces et actualités." : "Announcements and updates."}
      </p>

      <ul className="mt-12 divide-y divide-border border-y border-border">
        {posts.map((p: any) => {
          const slug = p.slug?.[locale]?.current || p.slug?.en?.current;
          const kind = kindLabel(p.kind, locale);
          return (
            <li key={p._id} className="py-6">
              <Link
                to="/news/$slug"
                params={{ slug }}
                search={search}
                className="group flex flex-col gap-4"
              >
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {kind && <span className="font-medium">{kind}</span>}
                  {kind && p.publishedAt && " · "}
                  {p.publishedAt && formatDate(p.publishedAt, locale)}
                </p>
                <h2 className="w-full text-2xl font-semibold group-hover:underline md:text-3xl">
                  {pickLocalized(p.title, locale)}
                </h2>
                <div className="grid items-start gap-6 md:grid-cols-[160px_1fr]">
                  {p.coverImage && (
                    <img
                      src={urlFor(p.coverImage).width(320).height(200).fit("crop").url()}
                      alt={
                        pickLocalized(p.coverImage?.alt, locale) || pickLocalized(p.title, locale)
                      }
                      className="h-28 w-full rounded-lg object-cover md:h-24"
                    />
                  )}
                  {p.excerpt && (
                    <p className="text-sm text-muted-foreground">
                      {pickLocalized(p.excerpt, locale)}
                    </p>
                  )}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
